import React, { useCallback, useEffect, useRef } from "react";
import ConsentDrawer from "../../components/FinFactorInvestment/ConsentDrawer";
import CommonConsentTable from "../../components/FinFactorInvestment/CommonConsentTable";
import {
    useGetConsentsQuery,
    useInitiateConsentMutation,
    useLazyGetVendorConsentStatusQuery,
} from "../../store/api/finfactor.apislice";
import { useNavigate } from "react-router-dom";
import ROUTES from "../../router/routes.constants";
import { isNative } from "../../utils/platform.utils";
import {
    openInWebView,
    closeInAppBrowser,
    addBrowserListeners,
    removeBrowserListeners,
} from "../../utils/browser.utils";
import { usePlanGate } from "../../hooks/usePlanGate";
import { useInvestGate } from "../../hooks/useInvestGate";
import { markFinFactorJourneyPending } from "../../utils/finfactorJourney.utils";

const POLLING_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 90;

const FinFactorConsetDrawerContainer = ({ open, onClose }) => {
    const navigate = useNavigate();
    const pollIntervalRef = useRef(null);
    const pollingWorkerRef = useRef(null);
    const audioContextRef = useRef(null);
    const consentHandlesRef = useRef([]);
    const isCheckingRef = useRef(false);
    const pollCountRef = useRef(0);
    const isConsentFlowActiveRef = useRef(false);

    const { data, isLoading, refetch: refetchConsents } = useGetConsentsQuery(
        undefined,
        {
            skip: !open,
            refetchOnMountOrArgChange: true,
            refetchOnFocus: true,
            refetchOnReconnect: true,
        },
    );
    const { consents = [], totalConsents = 0 } = data || {};

    const [pollVendorConsentStatus] = useLazyGetVendorConsentStatusQuery();

    const getNormalizedConsentStatus = useCallback((payload) => {
        const status =
            payload?.consentStatus ||
            payload?.status ||
            payload?.data?.consentStatus ||
            payload?.data?.status ||
            payload?.result?.consentStatus ||
            payload?.result?.status ||
            payload?.result?.data?.consentStatus ||
            payload?.result?.data?.status;

        return typeof status === "string" ? status.toUpperCase() : "";
    }, []);

    const isTerminalConsentStatus = useCallback(
        (payload) => {
            const normalized = getNormalizedConsentStatus(payload);
            return normalized === "APPROVED" || normalized === "REJECTED";
        },
        [getNormalizedConsentStatus],
    );

    const stopConsentPolling = useCallback(() => {
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
        if (pollingWorkerRef.current) {
            pollingWorkerRef.current.terminate();
            pollingWorkerRef.current = null;
        }
        if (audioContextRef.current) {
            audioContextRef.current.close().catch(() => {});
            audioContextRef.current = null;
        }
    }, []);

    const finishConsentFlow = useCallback(async () => {
        stopConsentPolling();
        removeBrowserListeners();
        isConsentFlowActiveRef.current = false;
        consentHandlesRef.current = [];
        pollCountRef.current = 0;

        await closeInAppBrowser().catch((err) =>
            console.error("Browser close error:", err),
        );

        // Dashboard picks this up and refetches every FinFactor API a few
        // times, since AA data keeps arriving after the consent turns terminal.
        markFinFactorJourneyPending();
        refetchConsents();
        onClose({ skipHistoryBack: true });
        navigate(ROUTES.FINANCIAL_OVERVIEW);
    }, [navigate, onClose, refetchConsents, stopConsentPolling]);

    const checkConsentStatus = useCallback(async () => {
        if (isCheckingRef.current || consentHandlesRef.current.length === 0) {
            return;
        }

        isCheckingRef.current = true;
        pollCountRef.current += 1;
        console.log(
            `Consent status polling attempt ${pollCountRef.current}`,
            consentHandlesRef.current,
        );

        try {
            const response = await pollVendorConsentStatus(
                consentHandlesRef.current,
            ).unwrap();

            const results = Array.isArray(response)
                ? response
                : [response];

            const terminalResult = results.find(isTerminalConsentStatus);

            if (terminalResult) {
                const finalStatus = getNormalizedConsentStatus(terminalResult);
                console.log(
                    `%c TERMINAL STATUS REACHED (DRAWER): ${finalStatus} `,
                    "background: #222; color: #bada55; font-size: 16px",
                );
                await finishConsentFlow();
                return;
            }

            if (pollCountRef.current >= MAX_POLL_ATTEMPTS) {
                console.warn("Consent polling max attempts reached");
                stopConsentPolling();
            }
        } catch (err) {
            console.error("Consent status polling error:", err);
            if (pollCountRef.current >= MAX_POLL_ATTEMPTS) {
                stopConsentPolling();
            }
        } finally {
            isCheckingRef.current = false;
        }
    }, [
        pollVendorConsentStatus,
        finishConsentFlow,
        isTerminalConsentStatus,
        getNormalizedConsentStatus,
        stopConsentPolling,
    ]);

    const startKeepAliveHeartbeats = useCallback(() => {
        try {
            const AudioCtx = window.AudioContext || window.webkitAudioContext;
            if (AudioCtx) {
                audioContextRef.current = new AudioCtx();
                const oscillator = audioContextRef.current.createOscillator();
                const gainNode = audioContextRef.current.createGain();
                oscillator.connect(gainNode);
                gainNode.connect(audioContextRef.current.destination);
                gainNode.gain.value = 0.001;
                oscillator.start();
            }
        } catch (e) {
            console.warn("Consent audio heartbeat failed", e);
        }

        try {
            const workerBlob = new Blob(
                [`setInterval(() => { self.postMessage('tick'); }, 5000);`],
                { type: "application/javascript" },
            );
            pollingWorkerRef.current = new Worker(
                URL.createObjectURL(workerBlob),
            );
            pollingWorkerRef.current.onmessage = () => checkConsentStatus();
        } catch (e) {
            console.warn("Consent worker heartbeat failed", e);
        }
    }, [checkConsentStatus]);

    const startConsentPolling = useCallback(() => {
        stopConsentPolling();
        pollCountRef.current = 0;
        startKeepAliveHeartbeats();

        pollIntervalRef.current = setInterval(
            checkConsentStatus,
            POLLING_INTERVAL_MS,
        );

        setTimeout(checkConsentStatus, 500);
    }, [checkConsentStatus, stopConsentPolling, startKeepAliveHeartbeats]);

    const closeConsentBrowserOnly = useCallback(async () => {
        removeBrowserListeners();
        await closeInAppBrowser().catch((err) =>
            console.error("Browser close error:", err),
        );
    }, []);

    const handleBrowserClosed = useCallback(() => {
        if (!isConsentFlowActiveRef.current) return;
        console.log(
            "Browser closed during consent flow (Drawer). Polling continues until terminal status.",
        );
        removeBrowserListeners();
        checkConsentStatus();
    }, [checkConsentStatus]);

    const handleNavigationCompleted = useCallback(
        (data) => {
            const url = data?.url || "";
            console.log("Drawer InAppBrowser Navigation:", url);

            const isTerminalRedirectUrl =
                url.includes("account-aggregator-service.inxits.com") ||
                url.includes("inxits-customer-app");

            if (isTerminalRedirectUrl) {
                console.log(
                    "Terminal URL reached (Drawer). Closing browser; polling continues until APPROVED/REJECTED.",
                );
                closeConsentBrowserOnly();
                checkConsentStatus();
            }
        },
        [closeConsentBrowserOnly, checkConsentStatus],
    );

    const startNativeConsentJourney = useCallback(
        async (redirectUrl, handles) => {
            consentHandlesRef.current = handles;
            isConsentFlowActiveRef.current = true;

            await addBrowserListeners(
                handleBrowserClosed,
                () => checkConsentStatus(),
                handleNavigationCompleted,
            );

            openInWebView(redirectUrl).catch((err) =>
                console.error("Failed to open consent webview:", err),
            );

            startConsentPolling();
        },
        [
            handleBrowserClosed,
            handleNavigationCompleted,
            checkConsentStatus,
            startConsentPolling,
        ],
    );

    useEffect(() => {
        return () => {
            stopConsentPolling();
            if (isNative()) {
                removeBrowserListeners();
            }
        };
    }, [stopConsentPolling]);

    const [initiateConsent, { isLoading: isInitiating }] =
        useInitiateConsentMutation();
    const { requirePlanSelection } = usePlanGate();
    const { guardInvest, isCheckingInvestGate } = useInvestGate();

    const startConsentJourney = useCallback(async () => {
        try {
            const payload = isNative() ? { isMobileJourney: true } : {};
            const response = await initiateConsent(payload).unwrap();
            console.log("Consent Initiation Response:", response);

            const redirectUrl = response?.result?.url || response?.result;

            if (redirectUrl && typeof redirectUrl === "string") {
                if (isNative()) {
                    const handles = response?.result?.consentHandles
                        ? Object.values(response.result.consentHandles)
                        : [];

                    if (handles.length > 0) {
                        await startNativeConsentJourney(redirectUrl, handles);
                    } else {
                        console.error("No consent handles received for polling");
                    }
                } else {
                    // Web journey leaves the SPA, so flag it before redirecting.
                    markFinFactorJourneyPending();
                    window.location.href = redirectUrl;
                }
            } else {
                console.error("Invalid redirect URL received");
            }
        } catch (error) {
            isConsentFlowActiveRef.current = false;
            stopConsentPolling();
            console.error("Initiate Consent Error:", error);
        }
    }, [initiateConsent, startNativeConsentJourney, stopConsentPolling]);

    // Same gating as the Invest button: the plan gate, then the full invest
    // gate (payment -> agreement -> risk -> onboarding) before the customer is
    // handed off to the account-aggregator consent journey.
    const handleGiveConsent = useCallback(async () => {
        if (requirePlanSelection()) return;

        await guardInvest(startConsentJourney);
    }, [requirePlanSelection, guardInvest, startConsentJourney]);

    return (
        <ConsentDrawer
            open={open}
            onClose={onClose}
            title={`Consent Given (${totalConsents})`}
            listTitle="List"
            buttonText="Give Consent"
            onButtonClick={handleGiveConsent}
            isLoading={isLoading || isInitiating || isCheckingInvestGate}
        >
            <CommonConsentTable
                columns={["FI Type", "Linked Accounts"]}
                rows={consents}
                showActionColumn={false}
                cellSx={{
                    py: 2.5,
                    width: "50%",
                }}
            />
        </ConsentDrawer>
    );
};

export default FinFactorConsetDrawerContainer;
